import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

export type LogDataEntry = {
  uuid: string;
  createdAt: string;
  updatedAt: string;
  contact: string | null;
  submissionIndex: number | null;
  otpVerified: boolean;
  name: string | null;
  race: string | null;
  state: string | null;
  tng_sent: boolean;
  staffName: string | null;
  staffCode: string | null;
  status: 'Completed' | 'OTP verified' | 'Session started';
};

export type LogActivity = {
  id: string;
  type:
    | 'session_started'
    | 'otp_verified'
    | 'survey_completed'
    | 'tng_sent'
    | 'qr_scan'
    | 'staff_registered'
    | 'staff_added';
  label: string;
  at: string;
  contact: string | null;
  staffName: string | null;
  staffCode: string | null;
  detail: string | null;
};

export type AdminLogsSummary = {
  dataEntryCount: number;
  activityCount: number;
  completedSurveys: number;
  qrScans: number;
};

export type AdminLogsResponse = {
  dataEntries: LogDataEntry[];
  activities: LogActivity[];
  summary: AdminLogsSummary;
};

export async function fetchAdminLogs(input: {
  startISO: string;
  endISO: string;
}): Promise<AdminLogsResponse> {
  const apiUrl = getApiOrigin();
  const params = new URLSearchParams({
    startDate: input.startISO,
    endDate: input.endISO,
  });

  const res = await fetch(`${apiUrl}/api/v1/admin/logs?${params.toString()}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });

  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    dataEntries?: LogDataEntry[];
    activities?: LogActivity[];
    summary?: AdminLogsSummary;
    error?: string;
  };

  if (!res.ok || !data.ok || !Array.isArray(data.dataEntries) || !Array.isArray(data.activities)) {
    throw new Error(data.error || `Failed to load logs (HTTP ${res.status})`);
  }

  return {
    dataEntries: data.dataEntries,
    activities: data.activities,
    summary: data.summary ?? {
      dataEntryCount: data.dataEntries.length,
      activityCount: data.activities.length,
      completedSurveys: 0,
      qrScans: 0,
    },
  };
}
